import {
  Component, ElementRef, EventEmitter, NgZone, OnInit,
  Output, Input, ViewChild, OnDestroy,
  ChangeDetectionStrategy
} from '@angular/core';

interface Grecaptcha {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      size?: 'invisible' | 'normal' | 'compact';
      callback?: (token: string) => void;
      'error-callback'?: () => void;
    }
  ) => number;
  reset: (widgetId?: number) => void;
  execute: (widgetId?: number) => void;
}

declare global {
  interface Window {
    grecaptcha: Grecaptcha;
  }
}

@Component({
  selector: 'app-recaptcha-v2',
  standalone: true,
  templateUrl: './recaptcha-v2.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  exportAs: 'appRecaptcha'
})
export class RecaptchaV2Component implements OnInit, OnDestroy {
  @ViewChild('captchaElem', { static: true }) captchaElem!: ElementRef;

  @Output() resolved = new EventEmitter<string>();
  @Output() error = new EventEmitter<string>();

  @Input() siteKey = '';

  private widgetId!: number;
  private maxRetries = 50;
  private retryDelay = 200; // ms
  private retryTimer?: ReturnType<typeof setTimeout>;
  private executionRequested = false;
  private destroyed = false;

  constructor(private zone: NgZone) { }

  ngOnInit(): void {
    this.tryRender(this.maxRetries);
  }

  private tryRender(remainingAttempts: number): void {
    this.retryTimer = undefined;
    if (this.destroyed) {
      return;
    }

    if (window.grecaptcha && window.grecaptcha.render) {
      try {
        this.widgetId = window.grecaptcha.render(this.captchaElem.nativeElement, {
          sitekey: this.siteKey,
          size: 'invisible',
          callback: (token: string) => {
            if (!this.destroyed) {
              this.zone.run(() => this.resolved.emit(token));
            }
          },
          'error-callback': () => this.fail('challenge-failed')
        });
      } catch {
        this.fail('render-failed');
        return;
      }
      if (this.executionRequested) {
        this.executeWidget();
      }
    } else if (remainingAttempts > 0) {
      this.retryTimer = setTimeout(() => this.tryRender(remainingAttempts - 1), this.retryDelay);
    } else {
      console.error('reCAPTCHA cannot be initialized: grecaptcha is not available.');
      this.fail('initialization-timeout');
    }
  }

  execute(): void {
    if (this.destroyed || this.executionRequested) {
      return;
    }

    this.executionRequested = true;
    if (this.widgetId === undefined) {
      // The async Google script may arrive after the initial render window.
      // Give each donation attempt a fresh, bounded initialization window.
      clearTimeout(this.retryTimer);
      this.tryRender(this.maxRetries);
      return;
    }
    this.executeWidget();
  }

  private executeWidget(): void {
    this.executionRequested = false;
    if (!window.grecaptcha || this.widgetId === undefined) {
      this.fail('unavailable');
      return;
    }

    try {
      // Tokens are single-use; every donor-save attempt needs a new one.
      window.grecaptcha.reset(this.widgetId);
      window.grecaptcha.execute(this.widgetId);
    } catch {
      this.fail('execution-failed');
    }
  }

  private fail(reason: string): void {
    this.executionRequested = false;
    if (!this.destroyed) {
      this.zone.run(() => this.error.emit(reason));
    }
  }

  reset(): void {
    if (window.grecaptcha && this.widgetId !== undefined) {
      window.grecaptcha.reset(this.widgetId);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.executionRequested = false;
    clearTimeout(this.retryTimer);
    this.reset();
  }
}
